import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTokens } from '../contexts/TokenContext';
import TokenIcon from './TokenIcon';
import WelcomeModal from './WelcomeModal';

interface LayoutProps {
  children: React.ReactNode;
}

// Tooltip wrapper for nav items
interface NavItemProps {
  to: string;
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  isCollapsed: boolean;
  onClick?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ to, label, icon, isActive, isCollapsed, onClick }) => {
  return (
    <div className="relative group">
      <Link
        to={to}
        onClick={onClick}
        className={`flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-3'} py-2 text-sm font-normal rounded-md transition-colors ${
          isActive 
            ? 'bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-400' 
            : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800'
        }`}
      >
        <span className={`${isCollapsed ? '' : 'mr-3'} flex-shrink-0`}>{icon}</span>
        {!isCollapsed && <span>{label}</span>}
      </Link>
      
      {/* Tooltip - CSS hover based, shows when collapsed */}
      {isCollapsed && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-[9999]">
          <div className="relative bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium px-3 py-1.5 rounded-lg shadow-xl whitespace-nowrap">
            <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 dark:bg-white rotate-45" />
            <span className="relative">{label}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { signOut, user, isAdmin, isActualAdmin, viewingAsUser, toggleViewAsUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { balance } = useTokens();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [headerSearch, setHeaderSearch] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });

  // Save collapse preference
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen flex bg-zinc-50 dark:bg-zinc-900">
      {/* Welcome Modal - shows on first login */}
      <WelcomeModal />
      
      {/* Mobile sidebar overlay */}
      <div 
        className={`fixed inset-0 bg-black z-20 md:hidden transition-opacity duration-200 ${sidebarOpen ? 'opacity-80 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar - Fixed position, sticky on desktop */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 ${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 flex flex-col z-30 transform transition-all duration-300 ease-out md:translate-x-0 border-r border-zinc-200 dark:border-zinc-800 ${sidebarCollapsed ? 'overflow-visible' : ''}`}>
        {/* Logo + Collapse Button */}
        <div className="flex-shrink-0 px-3 py-4 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 relative">
          <Link to="/grow" className={`flex items-center ${sidebarCollapsed ? 'gap-1 mx-auto pr-4' : 'gap-2.5 ml-2'}`}>
            <img 
              src={theme === 'dark' ? "/images/nova-logomark.png" : "/images/nova-logomark-black.png"}
              alt="Garden" 
              className={`${sidebarCollapsed ? 'h-6 w-6' : 'h-7 w-7'} object-contain`}
            />
            <span className={`${sidebarCollapsed ? 'text-lg' : 'text-xl'} font-semibold text-zinc-900 dark:text-zinc-100`}>
              {sidebarCollapsed ? 'G' : 'Garden'}
            </span>
          </Link>
          {!sidebarCollapsed && (
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors hidden md:flex"
              title="Collapse sidebar"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>
        
        {/* Expand button - outside header when collapsed */}
        {sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="absolute -right-3 top-16 p-1.5 rounded-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 shadow-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors hidden md:flex z-50"
            title="Expand sidebar"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Navigation - scrollable area */}
        <nav className={`flex-1 px-2 py-4 space-y-0.5 min-h-0 ${sidebarCollapsed ? 'overflow-visible' : 'overflow-y-auto'}`}>
          {/* HOME - Grow page */}
          <NavItem
            to="/grow"
            label="Grow"
            isActive={isActivePath('/grow')}
            isCollapsed={sidebarCollapsed}
            onClick={() => setSidebarOpen(false)}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8m0 0c-4 0-7-3-7-8 4 0 7 3 7 8zm0 0c4 0 7-3 7-8-4 0-7 3-7 8z" />
              </svg>
            }
          />

          {/* LIBRARY Section - Main content for all users */}
          {!sidebarCollapsed && (
            <div className="px-3 py-2 mt-4 text-xs font-medium text-zinc-500 dark:text-zinc-500 uppercase tracking-wider">
              LIBRARY
            </div>
          )}
          {sidebarCollapsed && <div className="my-4 border-t border-zinc-200 dark:border-zinc-800" />}
          <NavItem
            to="/modules"
            label="Modules"
            isActive={isActivePath('/modules')}
            isCollapsed={sidebarCollapsed}
            onClick={() => setSidebarOpen(false)}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            }
          />
          <NavItem
            to="/how-to"
            label="How-To Guides"
            isActive={isActivePath('/how-to')}
            isCollapsed={sidebarCollapsed}
            onClick={() => setSidebarOpen(false)}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
            }
          />
          
          {/* MY STATS Section - All users */}
          {!sidebarCollapsed && (
            <div className="px-3 py-2 mt-6 text-xs font-medium text-zinc-500 dark:text-zinc-500 uppercase tracking-wider">
              MY STATS
            </div>
          )}
          {sidebarCollapsed && <div className="my-4 border-t border-zinc-200 dark:border-zinc-800" />}
          <NavItem to="/my-analytics" label="My Analytics" isActive={isActivePath('/my-analytics')} isCollapsed={sidebarCollapsed} onClick={() => setSidebarOpen(false)}
            icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>}
          />

          {/* ADMIN Section - Admin only */}
          {isAdmin && (
            <>
              {!sidebarCollapsed && (
                <div className="px-3 py-2 mt-6 text-xs font-medium text-zinc-500 dark:text-zinc-500 uppercase tracking-wider">
                  ADMIN
                </div>
              )}
              {sidebarCollapsed && <div className="my-4 border-t border-zinc-200 dark:border-zinc-800" />}
              <NavItem to="/approvals" label="Content Approvals" isActive={isActivePath('/approvals')} isCollapsed={sidebarCollapsed} onClick={() => setSidebarOpen(false)}
                icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              />
              <NavItem to="/manage" label="Manage Modules" isActive={isActivePath('/manage')} isCollapsed={sidebarCollapsed} onClick={() => setSidebarOpen(false)}
                icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
              />
              <NavItem to="/users" label="Users" isActive={isActivePath('/users')} isCollapsed={sidebarCollapsed} onClick={() => setSidebarOpen(false)}
                icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>}
              />
              <NavItem to="/team-management" label="Categories" isActive={isActivePath('/team-management')} isCollapsed={sidebarCollapsed} onClick={() => setSidebarOpen(false)}
                icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 01-1.125-1.125v-3.75zM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-8.25zM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-2.25z" /></svg>}
              />
              <NavItem to="/admin/rewards" label="Manage Rewards" isActive={isActivePath('/admin/rewards')} isCollapsed={sidebarCollapsed} onClick={() => setSidebarOpen(false)}
                icon={<TokenIcon size="sm" />}
              />
              <NavItem to="/admin/analytics" label="Analytics" isActive={isActivePath('/admin/analytics')} isCollapsed={sidebarCollapsed} onClick={() => setSidebarOpen(false)}
                icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>}
              />
              <NavItem to="/admin/discovery" label="Discovery" isActive={isActivePath('/admin/discovery')} isCollapsed={sidebarCollapsed} onClick={() => setSidebarOpen(false)}
                icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>}
              />
            </>
          )}

          {/* REWARDS Section - Visible to all */}
          {!sidebarCollapsed && (
            <div className="px-3 py-2 mt-6 text-xs font-medium text-zinc-500 dark:text-zinc-500 uppercase tracking-wider">
              REWARDS
            </div>
          )}
          {sidebarCollapsed && <div className="my-4 border-t border-zinc-200 dark:border-zinc-800" />}
          <NavItem to="/rewards" label="Tokens & Rewards" isActive={isActivePath('/rewards')} isCollapsed={sidebarCollapsed} onClick={() => setSidebarOpen(false)}
            icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" /></svg>}
          />
        </nav>

        {/* User section - Always at bottom */}
        <div className="flex-shrink-0 p-3 border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
          <Link
            to="/profile"
            onClick={() => setSidebarOpen(false)}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors`}
            title="View Profile"
          >
            <img
              className={`${sidebarCollapsed ? 'h-8 w-8' : 'h-9 w-9'} rounded-full ring-1 ring-zinc-200 dark:ring-zinc-700 flex-shrink-0 object-cover`}
              src={
                user?.user_metadata?.avatar_url || 
                user?.user_metadata?.picture || 
                user?.identities?.[0]?.identity_data?.avatar_url ||
                user?.identities?.[0]?.identity_data?.picture ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || '')}&background=6366f1&color=fff`
              }
              alt="User avatar"
              referrerPolicy="no-referrer"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (!target.dataset.fallback) {
                  target.dataset.fallback = 'true';
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.user_metadata?.full_name || user?.email || '')}&background=6366f1&color=fff`;
                }
              }}
            />
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-normal text-zinc-900 dark:text-zinc-100 truncate">
                  {user?.user_metadata?.full_name || user?.email}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500">View Profile</span>
                  {isActualAdmin && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${viewingAsUser ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400' : 'bg-teal-100 dark:bg-teal-500/20 text-teal-700 dark:text-teal-400'}`}>
                      {viewingAsUser ? 'User View' : 'Admin'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </Link>
          {/* View as User toggle - only for John Saunders */}
          {!sidebarCollapsed && isActualAdmin && (
            user?.email?.toLowerCase().includes('johnsaunders') ||
            user?.user_metadata?.full_name?.toLowerCase().includes('john saunders')
          ) && (
            <button
              onClick={toggleViewAsUser}
              className={`mt-2 w-full text-left text-xs font-medium px-2 py-1.5 rounded transition-colors ${
                viewingAsUser 
                  ? 'bg-teal-100 dark:bg-teal-500/20 text-teal-700 dark:text-teal-400 hover:bg-teal-200 dark:hover:bg-teal-500/30' 
                  : 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-500/30'
              }`}
            >
              {viewingAsUser ? '← Back to Admin View' : 'View as User →'}
            </button>
          )}
          {!sidebarCollapsed && (
            <button
              onClick={signOut}
              className="mt-2 w-full text-left text-xs font-normal text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors px-2 py-1"
            >
              Sign out
            </button>
          )}
          {sidebarCollapsed && (
            <button
              onClick={signOut}
              className="mt-2 w-full flex justify-center text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors p-2"
              title="Sign out"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Main content - offset by sidebar width */}
      <div className={`flex-1 flex flex-col overflow-hidden ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'} transition-all duration-200`}>
        {/* Top header */}
        <header className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 z-10 sticky top-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 md:hidden transition-colors"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <span className="sr-only">Open sidebar</span>
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
            {/* Global Search */}
            <div className="hidden sm:block flex-1 max-w-md mx-4">
              <div className="relative">
                <input
                  type="text"
                  value={headerSearch}
                  onChange={(e) => setHeaderSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && headerSearch.trim()) {
                      navigate(`/modules?q=${encodeURIComponent(headerSearch.trim())}`);
                      setHeaderSearch('');
                    }
                  }}
                  placeholder="Search modules..."
                  className="block w-full pl-9 pr-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 bg-zinc-50 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 transition-colors"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {/* Token Balance */}
              <Link
                to="/rewards"
                className="inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 border border-zinc-200 dark:border-zinc-700 transition-colors"
                title="View your tokens and rewards"
              >
                <TokenIcon size="sm" className="mr-1.5 sm:mr-2" />
                <span className="font-semibold">{balance.toLocaleString()}</span>
              </Link>

              {/* User View Indicator - only for John Saunders */}
              {viewingAsUser && (
                user?.email?.toLowerCase().includes('johnsaunders') ||
                user?.user_metadata?.full_name?.toLowerCase().includes('john saunders')
              ) && (
                <button
                  onClick={toggleViewAsUser}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/20 hover:bg-amber-200 dark:hover:bg-amber-500/30 border border-amber-200 dark:border-amber-500/30 transition-colors"
                >
                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Viewing as User
                </button>
              )}

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="inline-flex items-center p-2 text-sm font-medium rounded-lg text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 border border-zinc-200 dark:border-zinc-700 focus:outline-none transition-colors"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>
              
              <button
                onClick={() => navigate('/create')}
                className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg text-white bg-teal-600 hover:bg-teal-700 dark:bg-teal-600 dark:hover:bg-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30 transition-colors"
              >
                <svg className="h-4 w-4 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="hidden sm:inline">New</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1 overflow-auto bg-zinc-50 dark:bg-zinc-900">
          <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 